import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, CardSubtitle, CardTitle } from '@keobook/ui';
import { type DashboardSummary } from '@keobook/shared';
import { api } from '../lib/api';
import { useMoneyFormatter } from '../lib/useCurrency';
import { env } from '../env';
import { Layout } from '../components/Layout';

const PLAN_LABEL: Record<string, string> = {
  starter: 'Starter',
  professional_plus: 'Professional+',
  business_plus: 'Business+',
};

export function Dashboard() {
  const fmt = useMoneyFormatter();
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [seeding, setSeeding] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      setSummary(await api.getDashboardSummary());
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not load your dashboard.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function onSeed() {
    setSeeding(true);
    try {
      await api.seedLedger();
      await load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not load sample data.');
    } finally {
      setSeeding(false);
    }
  }

  const name = summary?.companyName ?? 'your business';

  return (
    <Layout>
      <div className="kb-page" style={{ maxWidth: 980, margin: '0 auto', padding: '2rem 1.5rem' }}>
        <div style={{ marginBottom: '1.5rem' }}>
          <h1 style={{ fontSize: 26, fontWeight: 600, color: 'var(--kb-gray-900)', margin: 0 }}>
            {name}
          </h1>
          {summary && (
            <p style={{ color: 'var(--kb-gray-400)', fontSize: 13, margin: '4px 0 0 0' }}>
              {PLAN_LABEL[summary.planTier] ?? ''} plan ·{' '}
              {summary.kind === 'business' ? 'Business' : 'Individual'} engagement ·{' '}
              {summary.basis === 'cash' ? 'Cash basis' : 'Accrual basis'}
            </p>
          )}
        </div>

        {error && (
          <div role="alert" className="kb-form-error">
            {error}
          </div>
        )}

        {loading ? (
          <Card>
            <CardSubtitle>Loading your books…</CardSubtitle>
          </Card>
        ) : summary && summary.booksStarted ? (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: 12,
                marginBottom: '1.25rem',
              }}
            >
              <Metric
                label={`Income · ${summary.periodLabel}`}
                value={fmt(summary.incomeCents)}
              />
              <Metric
                label={`Expenses · ${summary.periodLabel}`}
                value={fmt(summary.expensesCents)}
              />
              <Metric
                label={`Net · ${summary.periodLabel}`}
                value={fmt(summary.netCents)}
                accent={summary.netCents >= 0 ? 'pos' : 'neg'}
              />
              <Metric label="Documents" value={String(summary.documentCount)} />
            </div>

            <Card>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  gap: 12,
                }}
              >
                <CardTitle>Your books</CardTitle>
                <span style={{ fontSize: 13, color: 'var(--kb-gray-400)' }}>
                  {summary.entryCount} entries · {summary.accountCount} accounts
                </span>
              </div>
              <CardSubtitle>
                {summary.kind === 'business' ? 'Business' : 'Individual'} books for{' '}
                {summary.periodLabel}.
              </CardSubtitle>
              <div style={{ display: 'flex', gap: 10, marginTop: '1rem', flexWrap: 'wrap' }}>
                <Link to="/reports" className="kb-button kb-button--primary kb-button--sm">
                  View Profit &amp; Loss
                </Link>
                <Link to="/activity" className="kb-button kb-button--ghost kb-button--sm">
                  Transaction activity
                </Link>
              </div>
            </Card>
          </>
        ) : (
          <Card>
            <CardTitle>Your books are ready to start</CardTitle>
            <CardSubtitle>
              Once transactions flow in, this dashboard shows your monthly income, expenses, and net
              — with reports your CPA reviews. Here's what's coming:
            </CardSubtitle>
            <ul
              style={{
                marginTop: '0.75rem',
                paddingLeft: '1.25rem',
                color: 'var(--kb-gray-600)',
                lineHeight: 1.8,
              }}
            >
              <li>
                <strong>Connect a bank</strong> or upload statements — transactions flow in
                automatically.
              </li>
              <li>
                <strong>AI categorizes</strong> everything against your seeded chart of accounts.
              </li>
              <li>
                <strong>Monthly reports</strong> — P&amp;L, Balance Sheet, Cash Flow — once a period
                closes.
              </li>
              <li>
                <strong>Pro+ / Business+</strong> add a CPA reviewing every entry before it posts.
              </li>
            </ul>
            {env.VITE_ENV !== 'production' && (
              <div style={{ marginTop: '1rem' }}>
                <Button size="sm" variant="ghost" disabled={seeding} onClick={onSeed}>
                  {seeding ? 'Loading sample books…' : 'Load sample books (preview)'}
                </Button>
              </div>
            )}
          </Card>
        )}
      </div>
    </Layout>
  );
}

function Metric({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: 'pos' | 'neg';
}) {
  const color =
    accent === 'pos'
      ? 'var(--kb-green-600)'
      : accent === 'neg'
        ? 'var(--kb-red-600)'
        : 'var(--kb-gray-900)';
  return (
    <Card>
      <div style={{ fontSize: 12, color: 'var(--kb-gray-400)', fontWeight: 500 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 600, color, marginTop: 4 }}>{value}</div>
    </Card>
  );
}
